/* eslint-disable class-methods-use-this */
import {decorate, observable} from 'mobx';
import Logger from "../Logger/Logger";
import PowerProxy from "../Models/PowerProxy";
import ConnectionStatus from "../Models/ConnectionStatus";
import HeaderTypes from "../Types/HeaderTypes";
import PowerClient from "../Models/PowerClient";
import Utils from "../Utils/Utils";

const logger = Logger.Create("PowerProxyRepository");

/**
 * Power Client Repository.
 */
export class PowerProxyRepository
{
    /**
     * The connected power clients.
     */
    public powerProxies: PowerProxy[] = [];

    public dummyPowerProxy: PowerProxy = {
        id: "",
        createdAt: undefined,
        name: "",
        isConnected: true,
    };

    private currentArrangement = {
        currentType: HeaderTypes.None,
        reverse: false,
    }

    /**
     * Updates the connected power proxies.
     * @param powerProxies The connected power proxies.
     */
    public updatePowerProxies(powerProxies: PowerProxy[]): void
    {
        this.powerProxies = powerProxies;
        this.orderArray(this.currentArrangement.currentType);
    }

    /**
     * Updates the power proxies connection status.
     * @param connectionStatuses The power proxies connection status.
     */
    public updatePowerProxyConnectionStatuses(connectionStatuses: ConnectionStatus[]): void
    {
        for (let i = 0; i < connectionStatuses.length; i++)
        {
            const matchingObject = this.powerProxies.find((item: PowerProxy) => item.id === connectionStatuses[i].id);
            
            if (matchingObject !== undefined)
            {
                matchingObject.isConnected = connectionStatuses[i].isConnected;
            }
        }
    }

    /**
     * Updates the power proxy array in the order of the header type.
     * @param headerType The header type.
     */
    public updateOrder(headerType: HeaderTypes): void
    {
        if (headerType === this.currentArrangement.currentType)
        {
            this.currentArrangement.reverse = !this.currentArrangement.reverse;
            this.orderArray(headerType);
            return;
        }

        this.orderArray(headerType);
    }

    private orderArray(headerType: HeaderTypes)
    {
        let shouldUpdate = false;

        switch (headerType)
        {
            case HeaderTypes.ProxyName:
                shouldUpdate = this.orderByProxyName();
                break;
            case HeaderTypes.ProxyId:
                shouldUpdate = this.orderByProxyId();
                break;
            case HeaderTypes.ConnectionStatus:
                shouldUpdate = this.orderByConnectionStatus();
                break;
            default:
            case HeaderTypes.None:
                break;
        }

        if (shouldUpdate)
        {
            this.currentArrangement.currentType = headerType;
        }
    }

    private orderByProxyName()
    {
        const value = this.currentArrangement.reverse ? -1 : 1;
        const clone = [...this.powerProxies];

        const allNames = clone.map((value: PowerProxy) => value.name);

        if (Utils.areAllEqual(allNames))
        {
            return false;
        }

        clone.sort((a, b) => a.name.toLowerCase() > b.name.toLowerCase()? value : -value);
        this.powerProxies = clone;

        return true;
    }

    private orderByProxyId()
    {
        const value = this.currentArrangement.reverse ? -1 : 1;
        const clone = [...this.powerProxies];

        const allNames = clone.map((value: PowerProxy) => value.id);

        if (Utils.areAllEqual(allNames))
        {
            return false;
        }

        clone.sort((a, b) => a.id.toLowerCase() > b.id.toLowerCase()? value : -value);
        this.powerProxies = clone;

        return true;
    }

    private orderByConnectionStatus()
    {
        const value = this.currentArrangement.reverse ? -1 : 1;
        const clone = [...this.powerProxies];

        const allConnectionStatus = clone.map((value: PowerProxy) => value.isConnected);

        if (Utils.areAllEqual(allConnectionStatus))
        {
            return false;
        }

        clone.sort((a, b) => a.isConnected > b.isConnected ? value : -value);
        this.powerProxies = clone;

        return true;
    }
}

decorate(PowerProxyRepository, {
    powerProxies: observable,
});

export const powerProxyRepository = new PowerProxyRepository();
